import React, { useState, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { medicationService } from '../services/apiServices';
import { Medication } from '../types';

interface MedicationFormDialogProps {
  open: boolean;
  onClose: () => void;
  medication?: Medication | null;
}

const MedicationFormDialog: React.FC<MedicationFormDialogProps> = ({
  open,
  onClose,
  medication,
}) => {
  const isEditMode = !!medication;
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({
    name: '',
    type: '',
    description: '',
    manufacturer: '',
    quantity_in_stock: 0,
    unit: '',
    unit_cost: 0,
    batch_number: '',
    manufacture_date: '',
    expiry_date: '',
    reorder_level: 0,
    storage_location: '',
    usage_instructions: '',
  });

  // Fetch medication types
  const { data: medicationTypes } = useQuery<string[]>({
    queryKey: ['medication-types'],
    queryFn: medicationService.getTypes,
  });

  useEffect(() => {
    if (medication) {
      setFormData({
        name: medication.name,
        type: medication.type,
        description: medication.description || '',
        manufacturer: medication.manufacturer || '',
        quantity_in_stock: medication.quantity_in_stock,
        unit: medication.unit,
        unit_cost: medication.unit_cost,
        batch_number: medication.batch_number || '',
        manufacture_date: medication.manufacture_date
          ? new Date(medication.manufacture_date).toISOString().split('T')[0]
          : '',
        expiry_date: medication.expiry_date
          ? new Date(medication.expiry_date).toISOString().split('T')[0]
          : '',
        reorder_level: medication.reorder_level,
        storage_location: medication.storage_location || '',
        usage_instructions: medication.usage_instructions || '',
      });
    } else {
      setFormData({
        name: '',
        type: '',
        description: '',
        manufacturer: '',
        quantity_in_stock: 0,
        unit: '',
        unit_cost: 0,
        batch_number: '',
        manufacture_date: '',
        expiry_date: '',
        reorder_level: 0,
        storage_location: '',
        usage_instructions: '',
      });
    }
  }, [medication, open]);

  const createMutation = useMutation({
    mutationFn: medicationService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['medications'] });
      queryClient.invalidateQueries({ queryKey: ['medication-statistics'] });
      queryClient.invalidateQueries({ queryKey: ['medication-types'] });
      handleClose();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => medicationService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['medications'] });
      queryClient.invalidateQueries({ queryKey: ['medication-statistics'] });
      handleClose();
    },
  });

  const handleChange = (field: string) => (e: any) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // Clean up empty strings
      const dataToSend: any = {};
      Object.entries(formData).forEach(([key, value]) => {
        if (value !== '' && value !== null) {
          dataToSend[key] = value;
        }
      });

      if (isEditMode && medication) {
        await updateMutation.mutateAsync({ id: medication.id, data: dataToSend });
      } else {
        await createMutation.mutateAsync(dataToSend);
      }
    } catch (error) {
      console.error('Failed to save medication:', error);
    }
  };

  const handleClose = () => {
    onClose();
  };

  // Calculate total cost
  const totalCost = formData.quantity_in_stock * formData.unit_cost;

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GH', {
      style: 'currency',
      currency: 'GHS',
    }).format(amount);
  };

  const medicationTypeOptions = [
    'Vaccine',
    'Antibiotic',
    'Supplement',
    'Dewormer',
    'Vitamin',
    'Disinfectant',
    'Probiotic',
    'Anti-stress',
    'Anticoccidial',
    'Other',
  ];

  const unitOptions = [
    'ml',
    'L',
    'tablets',
    'capsules',
    'sachets',
    'bottles',
    'vials',
    'kg',
    'g',
    'doses',
  ];

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEditMode ? 'Edit Medication' : 'Add New Medication'}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                required
                label="Medication Name"
                value={formData.name}
                onChange={handleChange('name')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                required
                select
                label="Type"
                value={formData.type}
                onChange={handleChange('type')}
              >
                {medicationTypeOptions.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
                {medicationTypes
                  ?.filter((type) => !medicationTypeOptions.includes(type))
                  .map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Description"
                value={formData.description}
                onChange={handleChange('description')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Manufacturer"
                value={formData.manufacturer}
                onChange={handleChange('manufacturer')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Batch Number"
                value={formData.batch_number}
                onChange={handleChange('batch_number')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                required
                type="number"
                label="Quantity in Stock"
                value={formData.quantity_in_stock}
                onChange={handleChange('quantity_in_stock')}
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                required
                select
                label="Unit"
                value={formData.unit}
                onChange={handleChange('unit')}
              >
                {unitOptions.map((unit) => (
                  <MenuItem key={unit} value={unit}>
                    {unit}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                required
                type="number"
                label="Unit Cost (GHS)"
                value={formData.unit_cost}
                onChange={handleChange('unit_cost')}
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>

            {/* Total Cost Display */}
            <Grid size={{ xs: 12 }}>
              <Alert
                severity="info"
                icon={false}
                sx={{
                  backgroundColor: 'primary.main',
                  color: 'white',
                  '& .MuiAlert-message': { width: '100%' },
                }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="h6" component="span" fontWeight="bold">
                    Total Inventory Cost:
                  </Typography>
                  <Typography variant="h4" component="span" fontWeight="bold">
                    {formatCurrency(totalCost)}
                  </Typography>
                </Box>
                <Typography variant="caption" sx={{ opacity: 0.9, display: 'block', mt: 0.5 }}>
                  ({formData.quantity_in_stock} {formData.unit} {'×'}{' '}
                  {formatCurrency(formData.unit_cost)} per {formData.unit})
                </Typography>
              </Alert>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                type="date"
                label="Manufacture Date"
                value={formData.manufacture_date}
                onChange={handleChange('manufacture_date')}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                type="date"
                label="Expiry Date"
                value={formData.expiry_date}
                onChange={handleChange('expiry_date')}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                type="number"
                label="Reorder Level"
                value={formData.reorder_level}
                onChange={handleChange('reorder_level')}
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Storage Location"
                value={formData.storage_location}
                onChange={handleChange('storage_location')}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Usage Instructions"
                value={formData.usage_instructions}
                onChange={handleChange('usage_instructions')}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            type="submit"
            variant="contained"
            disabled={createMutation.isPending || updateMutation.isPending}
          >
            {isEditMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default MedicationFormDialog;
